import axios from 'axios';

// Load API URL from runtime config file (can be changed after build!)
let API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Fetch runtime configuration on app load
let configLoaded = false;
const loadConfig = async () => {
  if (configLoaded) return API_URL;

  try {
    const response = await fetch('/config.json');
    const config = await response.json();
    API_URL = config.API_URL || API_URL;
    configLoaded = true;
    console.log('✅ Runtime config loaded. API URL:', API_URL);
  } catch (error) {
    console.warn('⚠️ Could not load runtime config, using fallback:', API_URL);
  }

  return API_URL;
};

class InventoryService {
  constructor() {
    // Load config when service is created
    loadConfig();
  }

  // Helper to ensure config is loaded before API calls
  async ensureConfigLoaded() {
    return await loadConfig();
  }
  /**
   * Fetch inventory data with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.item - Item number or description to search
   * @param {string} params.branch - Branch/store code to filter
   * @returns {Promise} API response with inventory data
   */
  async getInventory({ item = null, branch = null, page = 1, page_size = 5000 } = {}) {
    try {
      // Ensure config is loaded before making API call
      await this.ensureConfigLoaded();

      const params = {
        page,
        page_size,
      };
      if (item) params.item = item;
      if (branch) params.branch = branch;

      const response = await axios.get(`${API_URL}/inventory`, {
        params,
        timeout: 30000 // 30 second timeout
      });

      return response.data;
    } catch (error) {
      console.error('Inventory fetch error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get cache statistics from backend
   * @returns {Promise} Cache stats
   */
  async getCacheStats() {
    try {
      const response = await axios.get(`${API_URL}/cache/stats`);
      return response.data;
    } catch (error) {
      console.error('Cache stats error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Clear backend cache
   * @returns {Promise} Clear cache response
   */
  async clearCache() {
    try {
      const response = await axios.get(`${API_URL}/cache/clear`);
      return response.data;
    } catch (error) {
      console.error('Clear cache error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Health check
   * @returns {Promise} Health status
   */
  async healthCheck() {
    try {
      const response = await axios.get(`${API_URL}/`);
      return response.data;
    } catch (error) {
      console.error('Health check error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object from API call
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const errorMessage = error.response.data.detail || 'Server error occurred';
      const err = new Error(errorMessage);
      err.status = error.response.status;
      err.data = error.response.data;
      return err;
    } else if (error.request) {
      // Request made but no response
      const err = new Error('Unable to connect to server. Please check your connection.');
      err.status = 0;
      err.isNetworkError = true;
      return err;
    } else {
      // Other errors
      const err = new Error(error.message || 'An unexpected error occurred');
      err.status = -1;
      return err;
    }
  }
}

const inventoryServiceInstance = new InventoryService();
export default inventoryServiceInstance;
